import { Fragment, useState } from "react";

import { createColumnHelper, flexRender, getCoreRowModel, getExpandedRowModel, getSortedRowModel, SortingState, useReactTable } from "@tanstack/react-table";

import PartImage from "../stock/PartImage";
import UpdateTaxRate from "./UpdateTaxRate";
import useBaskets from "../../data/useBaskets";
import classNames from "../../utils/classNames";
import ClickToCopy from "../shared/ClickToCopy";
import { IBasketItem } from "../../models/Basket";
import PriceFormat from "../../utils/priceFormat";
import { FormatType } from "../../models/EbayOffer";
import openInNewTab from "../../utils/openInNewTab";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import BasketPriceEditor from "./BasketPriceEditor";


export default function BasketTable({ data }: { data: IBasketItem[] }) {
  const { removeItem } = useBaskets("BasketTable");
  const [sorting, setSorting] = useState<SortingState>([]);


  const colHelper = createColumnHelper<IBasketItem>();

  const calculatePrice = (basketItem: IBasketItem) => {
    if (!basketItem.part.offers || basketItem.part.offers.length === 0) {
      return "N/A";
    }

    if (basketItem.part.offers[0].format === FormatType.FixedPrice) {
      return PriceFormat(basketItem.part.offers[0].buyItNowPrice);
    } else {
      return PriceFormat(basketItem.part.offers[0].auctionStartPrice);
    }
  };

  const cols = [
    colHelper.display({
      id: "images",
      cell: (info) => <PartImage part={info.row.original.part} width="min-w-10 max-w-32" />,
    }),
    colHelper.accessor("part.category.name", {
      header: "Part Category",
      sortingFn: "auto",
    }),
    colHelper.accessor("part.tagNumber", {
      header: "Tag No.",
      sortingFn: "auto",
    }),
    colHelper.accessor("part.partNumber", {
      header: "Part No.",
      sortingFn: "auto",
      cell: (info) => <ClickToCopy text={info.getValue()} />,
    }),
    colHelper.accessor("part.vehicle.vrm", {
      header: "Vehicle",
      id: "vrm",
      sortingFn: "auto",
      cell: (info) => <div className="flex flex-col">
        <ClickToCopy text={info.row.original.part.vehicle?.vrm} />
        <span>{info.row.original.part.vehicle?.combinedMake ?? ""}</span>
        <span>{info.row.original.part.vehicle?.combinedModel ?? ""}</span>
        <ClickToCopy text={info.row.original.part.vehicle?.combinedVIN} />
      </div>,
    }),
    colHelper.accessor("part.offers", {
      id: "price",
      header: "Price",
      enableSorting: false,
      cell: (info) => <BasketPriceEditor basketItem={info.row.original} />,
    }),
    colHelper.accessor("taxRate", {
      header: "Tax Rate",
      sortingFn: "auto",
      cell: (info) => <UpdateTaxRate basketItem={info.row.original} />,
    }),
    // colHelper.accessor("part.grade", {
    //   header: "Grade",
    //   sortingFn: "auto",
    // }),
    // colHelper.accessor("part.vehicle.odometerInMiles", {
    //   header: "Mileage",
    //   sortingFn: "auto",
    // }),
    colHelper.display({
      id: "actions",
      header: "Actions",
      cell: (info) => (
        <div className="flex gap-x-2">
          <ButtonInput
            label="View Part"
            onClick={() => openInNewTab(`stock/${info.row.original.part.id}`)}
            classes={"!px-2 !py-1"}
          />
          <ButtonInput
            label="Remove"
            onClick={() => removeItem.mutate(info.row.original.part.id)}
            classes={"!px-2 !py-1 !bg-red-600 hover:!bg-red-700"}
          />
        </div>
      ),
    })
  ];


  const table = useReactTable({
    data: data,
    columns: cols,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="mt-4 bg-white overflow-auto">
      <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
              {headerGroup.headers.map((header, i) => (
                <th
                  key={header.id}
                  scope="col"
                  className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                >
                  {header.isPlaceholder ? null : (
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : "",
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        asc: " ▲",
                        desc: " ▼",
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, i) => {
            let rowCells = row.getVisibleCells();
            return (
              <Fragment key={i}>
                <tr
                  key={row.id}
                  // onClick={() => openInNewTab(`leads/${row.original.id}`)}
                  className={classNames(
                    "hover:bg-gray-100"
                  )}
                >
                  {rowCells.map((cell) => (
                    <td key={cell.id} className="px-3 py-3 border text-sm">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  )
}